import React from 'react';
import { motion } from 'framer-motion';
import {
  Scissors,
  Printer,
  Sparkles,
  RefreshCw,
  Sliders,
  ShieldCheck,
  PackageCheck,
  Ship,
  Check
} from 'lucide-react';

const StageVisualAnimation = ({ stepId }) => {
  switch (stepId) {
    case 'cutting':
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          {/* Animated Fabric Roll */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Scissors size={24} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: 'var(--accent-magenta)' }}>AUTOMATED LASER & FABRIC CUTTER</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(224, 0, 135, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#ffffff', fontWeight: 700 }}>SPEED: 1200mm/s</div>
          </div>

          {/* Cutting Table Visual Graphic */}
          <div style={{ position: 'relative', margin: '20px 0', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
            {/* Fabric Roll Graphic */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                style={{ width: '44px', height: '44px', borderRadius: '50%', border: '4px dashed var(--accent-magenta)', background: '#334155', flexShrink: 0 }}
              />
              <div style={{ flexGrow: 1, height: '8px', background: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)', borderRadius: '4px' }} />
            </div>

            {/* Laser Beam Sliding Across Fabric */}
            <div style={{ position: 'relative', height: '120px', background: '#1e293b', borderRadius: '6px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
              {/* Pattern Outlines */}
              <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
                <rect x="20" y="20" width="100" height="80" rx="8" fill="none" stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" />
                <rect x="140" y="20" width="120" height="80" rx="8" fill="none" stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" />
              </svg>

              {/* Moving Cutter Laser Beam */}
              <motion.div
                animate={{ x: ['5%', '90%', '5%'], y: ['10%', '60%', '10%'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'absolute', top: 0, left: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <div style={{ width: '20px', height: '20px', background: 'var(--accent-magenta)', borderRadius: '4px', boxShadow: '0 0 16px var(--accent-magenta)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '10px', fontWeight: 900 }}>CUT</div>
                <div style={{ width: '2px', height: '100px', background: 'linear-gradient(to bottom, var(--accent-magenta), transparent)', boxShadow: '0 0 12px var(--accent-magenta)' }} />
              </motion.div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Denim Roll Unrolling</span>
            <span>• Multi-Ply Blade Calibrated</span>
            <span>• Accuracy: ±0.2mm</span>
          </div>
        </div>
      );

    case 'printing':
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Printer size={24} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: 'var(--accent-magenta)' }}>ROTARY SCREEN & POCKET BRAND PRINTING</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(224, 0, 135, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#ffffff', fontWeight: 700 }}>INK: ECO-PIGMENT</div>
          </div>

          {/* Roller Press Animation Graphic */}
          <div style={{ position: 'relative', margin: '20px 0', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            {/* Spinning Printing Rollers */}
            <div style={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent-purple), var(--accent-magenta))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 16px var(--accent-glow)' }}
              >
                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#0f172a' }} />
              </motion.div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--accent-magenta)', letterSpacing: '0.1em' }}>PRESSING BRANDING</span>
                <div style={{ width: '80px', height: '3px', background: 'var(--accent-magenta)', borderRadius: '2px' }} />
              </div>

              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent-magenta), var(--accent-purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 16px var(--accent-glow)' }}
              >
                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#0f172a' }} />
              </motion.div>
            </div>

            {/* Printed Pocket Graphic Sliding */}
            <div style={{ position: 'relative', width: '100%', height: '70px', background: '#1e293b', borderRadius: '6px', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
              <motion.div
                animate={{ x: ['-20%', '100%'] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'linear' }}
                style={{ display: 'flex', alignItems: 'center', gap: '24px', whiteSpace: 'nowrap' }}
              >
                <div style={{ padding: '8px 16px', background: '#334155', border: '1px solid var(--accent-magenta)', borderRadius: '4px', fontSize: '11px', fontWeight: 900, color: '#fff' }}>
                  <span className="brand-name">RAYYAN DENIM</span> ★ 100% COTTON
                </div>
                <div style={{ padding: '8px 16px', background: '#334155', border: '1px solid var(--accent-magenta)', borderRadius: '4px', fontSize: '11px', fontWeight: 900, color: '#fff' }}>
                  <span className="brand-name">RAYYAN DENIM</span> ★ 100% COTTON
                </div>
              </motion.div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Pocket Liner Graphics</span>
            <span>• Care Label Stamp</span>
            <span>• Zero Bleed Ink</span>
          </div>
        </div>
      );

    case 'stitching':
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Sparkles size={24} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: 'var(--accent-magenta)' }}>HEAVY-DUTY TWIN-NEEDLE SEAMING</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(224, 0, 135, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#ffffff', fontWeight: 700 }}>THREAD: POLY-CORE 100%</div>
          </div>

          {/* Sewing Needle & Seam Stitch Animation */}
          <div style={{ position: 'relative', margin: '20px 0', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ position: 'relative', height: '100px', background: '#1e3a8a', borderRadius: '6px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Gold & Magenta Stitch Seam Lines */}
              <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
                <line x1="0" y1="40" x2="100%" y2="40" stroke="#f59e0b" strokeWidth="3" strokeDasharray="10 8" />
                <line x1="0" y1="60" x2="100%" y2="60" stroke="var(--accent-magenta)" strokeWidth="3" strokeDasharray="10 8" />
              </svg>

              {/* Pulsing Sewing Machine Needle */}
              <motion.div
                animate={{ y: [-15, 15, -15] }}
                transition={{ duration: 0.25, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'absolute', left: '50%', top: '20%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <div style={{ width: '6px', height: '40px', background: '#e2e8f0', borderRadius: '2px', boxShadow: '0 0 10px #ffffff' }} />
                <div style={{ width: '2px', height: '20px', background: '#e00087', boxShadow: '0 0 12px #e00087' }} />
              </motion.div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Double Lap Seams</span>
            <span>• Reinforced Bar-Tacks</span>
            <span>• Stress Point Lock</span>
          </div>
        </div>
      );

    case 'washing':
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <RefreshCw size={24} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: 'var(--accent-magenta)' }}>INDUSTRIAL OZONE & ENZYME WASH DRUM</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(224, 0, 135, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#ffffff', fontWeight: 700 }}>ECO WATER-SAVING</div>
          </div>

          {/* Rotary Washing Drum Visual Graphic */}
          <div style={{ position: 'relative', margin: '20px 0', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'center' }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              style={{ width: '140px', height: '140px', borderRadius: '50%', border: '6px dashed var(--accent-magenta)', background: 'radial-gradient(circle, #2563eb 0%, #1e3a8a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', boxShadow: '0 0 30px rgba(37, 99, 235, 0.4)' }}
            >
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#0f172a', border: '3px solid #ffffff' }} />
              {/* Washing Water Bubbles */}
              <motion.div animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.5, repeat: Infinity }} style={{ position: 'absolute', top: '20px', left: '30px', width: '12px', height: '12px', borderRadius: '50%', background: '#93c5fd' }} />
              <motion.div animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.9, 0.3] }} transition={{ duration: 2, repeat: Infinity }} style={{ position: 'absolute', bottom: '25px', right: '30px', width: '16px', height: '16px', borderRadius: '50%', background: '#60a5fa' }} />
            </motion.div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Laser Whiskering</span>
            <span>• Soft Touch Wash</span>
            <span>• Controlled Indigo Shade</span>
          </div>
        </div>
      );

    case 'finishing':
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Sliders size={24} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: 'var(--accent-magenta)' }}>AUTOMATIC HARDWARE & RIVET PRESS</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(224, 0, 135, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#ffffff', fontWeight: 700 }}>HARDWARE: ANTIQUE BRASS</div>
          </div>

          <div style={{ position: 'relative', margin: '20px 0', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
            {/* Shank Button Animation */}
            <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.2, repeat: Infinity }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', border: '3px solid #fef3c7', boxShadow: '0 0 16px rgba(217, 119, 6, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '9px', fontWeight: 900 }}>
                RAYYAN
              </div>
              <span style={{ fontSize: '11px', fontWeight: 700 }}>BRASS SHANK</span>
            </motion.div>

            {/* Leather Patch Animation */}
            <div style={{ padding: '10px 18px', background: '#78350f', borderRadius: '6px', border: '1px dashed #f59e0b', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span className="brand-name" style={{ fontSize: '11px', fontWeight: 900, color: '#fef3c7', letterSpacing: '0.1em' }}>RAYYAN DENIM</span>
              <span style={{ fontSize: '9px', color: '#fcd34d' }}>GENUINE LEATHER</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Keyhole Buttonholes</span>
            <span>• Jacron Patch Stamping</span>
            <span>• High Tension Rivets</span>
          </div>
        </div>
      );

    case 'quality':
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ShieldCheck size={24} style={{ color: '#25d366' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: '#25d366' }}>100% SPEC & NEEDLE SCAN INSPECTION</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(37, 211, 102, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#25d366', fontWeight: 700 }}>STATUS: 100% PASSED</div>
          </div>

          {/* Laser HUD Inspection Frame */}
          <div style={{ position: 'relative', margin: '20px 0', padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px stroke rgba(37, 211, 102, 0.4)', height: '130px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Sweeping Inspection HUD Line */}
            <motion.div
              animate={{ y: ['-100%', '100%', '-100%'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{ position: 'absolute', left: 0, width: '100%', height: '4px', background: '#25d366', boxShadow: '0 0 16px #25d366' }}
            />

            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(37, 211, 102, 0.15)', padding: '8px 14px', borderRadius: '6px', border: '1px solid #25d366' }}>
                <Check size={18} style={{ color: '#25d366' }} />
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>SEAM TENSION OK</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(37, 211, 102, 0.15)', padding: '8px 14px', borderRadius: '6px', border: '1px solid #25d366' }}>
                <Check size={18} style={{ color: '#25d366' }} />
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>MEASUREMENT OK</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Metal Detector Scan</span>
            <span>• Shade Matching Audit</span>
            <span>• Zero Defect Guarantee</span>
          </div>
        </div>
      );

    case 'packing':
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <PackageCheck size={24} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: 'var(--accent-magenta)' }}>EXPORT POLYBAGGING & MASTER CARTONING</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(224, 0, 135, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#ffffff', fontWeight: 700 }}>BARCODE INDEXED</div>
          </div>

          {/* Carton Box Graphic Animation */}
          <div style={{ position: 'relative', margin: '20px 0', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'center' }}>
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ width: '160px', padding: '16px', background: '#78350f', borderRadius: '8px', border: '2px stroke #f59e0b', boxShadow: '0 12px 24px rgba(0,0,0,0.3)', color: '#fef3c7', textTransform: 'uppercase', textAlign: 'center' }}
            >
              <div className="brand-name" style={{ fontSize: '12px', fontWeight: 900, marginBottom: '4px' }}>RAYYAN DENIM</div>
              <div style={{ fontSize: '9px', color: '#fcd34d', marginBottom: '8px' }}>EXPORT MASTER BATCH</div>
              <div style={{ background: '#ffffff', height: '24px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontSize: '10px', fontWeight: 800, letterSpacing: '0.15em' }}>
                |||| ||| |||||||
              </div>
            </motion.div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Individual Polybags</span>
            <span>• Silica Moisture Protection</span>
            <span>• Assorted Ratio Packaging</span>
          </div>
        </div>
      );

    case 'global':
    default:
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '340px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '12px', padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Ship size={24} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', color: 'var(--accent-magenta)' }}>GLOBAL PORT FREIGHT & CONTAINER DISPATCH</span>
            </div>
            <div style={{ fontSize: '11px', background: 'rgba(224, 0, 135, 0.2)', padding: '4px 10px', borderRadius: '99px', color: '#ffffff', fontWeight: 700 }}>WORLDWIDE SHIPPING</div>
          </div>

          {/* Global Freight Shipping Route Graphic Animation */}
          <div style={{ position: 'relative', margin: '20px 0', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', height: '120px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
              <path d="M 10 60 Q 150 10 300 60 T 600 60" fill="none" stroke="rgba(224, 0, 135, 0.4)" strokeWidth="3" strokeDasharray="8 6" />
            </svg>

            {/* Ship Sailing Across Ocean Route */}
            <motion.div
              animate={{ x: ['-40%', '40%'] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(15, 23, 42, 0.9)', padding: '8px 16px', borderRadius: '99px', border: '1px solid var(--accent-magenta)' }}
            >
              <Ship size={20} style={{ color: 'var(--accent-magenta)' }} />
              <span style={{ fontSize: '12px', fontWeight: 800 }}>DISPATCHING TO GLOBAL BUYERS</span>
            </motion.div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
            <span>• Port Customs Ready</span>
            <span>• Tracking & Freight Docs</span>
            <span>• Delivered to Port</span>
          </div>
        </div>
      );
  }
};

export default StageVisualAnimation;
